import { type ReactElement, useMemo } from 'react';
import { getText } from 'i18n/intl';
import { Icon } from '@timeedit/ui-components';
import { Button, Flex, Result, Typography } from '@timeedit/ui-components/antd';

type TErrorData = {
  hasError: boolean;
  isLoading: boolean;
  message: string;
  fetchAction: () => void;
};

type TErrorResultProps = {
  errors: TErrorData[];
  children: ReactElement | ReactElement[];
};

export const ErrorResult = ({
  errors,
  children,
}: TErrorResultProps): ReactElement => {
  const actualErrors = useMemo(
    () => errors.filter(({ hasError }) => hasError),
    [errors]
  );

  const isLoading = useMemo(
    () => actualErrors.some(({ isLoading }) => isLoading),
    [actualErrors]
  );

  if (actualErrors.length === 0) {
    return <>{children}</>;
  }

  return (
    <Result
      title={getText(
        'data_manager.study_combinations_mapping.page.error_result.title'
      )}
      subTitle={getText(
        'data_manager.study_combinations_mapping.page.error_result.subtitle'
      )}
      icon={
        <Flex align="center" justify="center">
          <Icon id="Error" alt="Error" />
        </Flex>
      }
      extra={
        <Button
          type="primary"
          loading={isLoading}
          onClick={() => {
            actualErrors.forEach(({ fetchAction }) => fetchAction());
          }}
          icon={<Icon id="ReloadOutlined" alt="Reload" />}
        >
          {getText(
            'data_manager.study_combinations_mapping.page.error_result.actions.retry'
          )}
        </Button>
      }
    >
      <Typography.Paragraph strong>
        {`${getText(
          'data_manager.study_combinations_mapping.page.error_result.error_list'
        )}:`}
      </Typography.Paragraph>
      {actualErrors.map(({ message }, index) => (
        <Flex key={index} className={'te-gap-2'} align="center">
          <Icon id="Error" alt="Error" />
          <Typography.Text>{message}</Typography.Text>
        </Flex>
      ))}
    </Result>
  );
};
